import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import {
  CREATE_GIST_WITH_CONTENT,
  GET_ALL_GISTS,
  GET_ALL_SUBJECTS,
  GET_GISTS_BY_SUBJECT,
  GET_MY_DRAFT_GISTS,
} from "../../../services/graphql/queriesMutations";
import styles from "../SubjectSinglePage.module.css";
import type { GistItem, SubjectItem } from "../model/types";

const MAX_LINK_LABEL_LENGTH = 120;
const MAX_DRAFT_TITLE_LENGTH = 160;
const MAX_DRAFT_SUMMARY_LENGTH = 260;

type SubjectTreeRow = {
  subject: SubjectItem;
  depth: number;
};

type CreateDraftResult = {
  createGistWithContent: {
    id: number;
    publicId?: string | null;
    title: string;
    slug?: string | null;
    subjectId: number;
    parentId?: number | null;
    status: string;
  };
};

type CreateDraftVariables = {
  input: {
    title: string;
    subjectId: number;
    parentId?: number;
    point: string;
    body: string;
    status: "DRAFT";
  };
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function normalizeBodyHtml(value: string): string {
  return value.trim();
}

function buildLinkParagraph(label: string, href: string): string {
  return `<p><a href="${escapeHtml(href)}" class="rich-inline-link">${escapeHtml(label)}</a></p>`;
}

function appendLinkToBody(bodyDraft: string, linkHtml: string): string {
  const trimmed = normalizeBodyHtml(bodyDraft);
  if (!trimmed) return linkHtml;
  return `${trimmed}${linkHtml}`;
}

function isDraftGist(gist: GistItem): boolean {
  return gist.status?.toUpperCase() === "DRAFT";
}

function truncateClean(value: string, maxLength: number): string {
  const clean = value.trim().replace(/\s+/g, " ");
  if (clean.length <= maxLength) return clean;
  return `${clean.slice(0, maxLength - 1).trim()}…`;
}

function buildDraftSummary(title: string, customSummary: string): string {
  const cleanSummary = truncateClean(customSummary, MAX_DRAFT_SUMMARY_LENGTH);
  if (cleanSummary) return cleanSummary;
  return `Draft section for ${title}.`;
}

function buildDraftBody(title: string): string {
  return `<p>Draft placeholder for ${escapeHtml(title)}. Continue writing this section here.</p>`;
}

function buildSubjectBuckets(subjects: SubjectItem[]): Record<string, SubjectItem[]> {
  const buckets: Record<string, SubjectItem[]> = {};
  subjects.forEach((subject) => {
    const key = String(subject.parentId ?? "root");
    if (!buckets[key]) buckets[key] = [];
    buckets[key].push(subject);
  });

  Object.keys(buckets).forEach((key) => {
    buckets[key].sort((a, b) => a.title.localeCompare(b.title));
  });

  return buckets;
}

function buildParentBySubjectId(subjects: SubjectItem[]): Map<number, number | null> {
  return new Map(subjects.map((subject) => [subject.id, subject.parentId ?? null]));
}

function getSubjectAncestorIds(
  subjectId: number | null | undefined,
  parentBySubjectId: Map<number, number | null>,
): number[] {
  if (typeof subjectId !== "number") return [];

  const path: number[] = [];
  const visited = new Set<number>();
  let currentId: number | null | undefined = subjectId;

  while (typeof currentId === "number" && !visited.has(currentId)) {
    visited.add(currentId);
    path.unshift(currentId);
    currentId = parentBySubjectId.get(currentId) ?? null;
  }

  return path;
}

function buildVisibleSubjectRows(
  subjects: SubjectItem[],
  expandedSubjectIds: Set<number>,
): SubjectTreeRow[] {
  const buckets = buildSubjectBuckets(subjects);
  const rows: SubjectTreeRow[] = [];

  const visit = (subject: SubjectItem, depth: number) => {
    rows.push({ subject, depth });
    if (!expandedSubjectIds.has(subject.id)) return;
    (buckets[String(subject.id)] || []).forEach((child) => visit(child, depth + 1));
  };

  (buckets.root || []).forEach((subject) => visit(subject, 0));
  return rows;
}

function getSubjectDirectGistCount(gists: GistItem[], subjectId: number): number {
  return gists.filter((gist) => gist.subjectId === subjectId).length;
}

function getInitialSubjectId(
  currentSubjectId: number | null | undefined,
  candidates: GistItem[],
): number | null {
  if (typeof currentSubjectId === "number") return currentSubjectId;
  const firstSubjectGist = candidates.find((gist) => typeof gist.subjectId === "number");
  return typeof firstSubjectGist?.subjectId === "number" ? firstSubjectGist.subjectId : null;
}

type InternalGistLinkComposerProps = {
  currentGistId: number;
  gists: GistItem[];
  subjects?: SubjectItem[];
  currentSubjectId?: number | null;
  bodyDraft: string;
  disabled?: boolean;
  getGistLinkUrl: (gist: GistItem) => string;
  onBodyDraftChange: React.Dispatch<React.SetStateAction<string>>;
};

export const InternalGistLinkComposer: React.FC<InternalGistLinkComposerProps> = ({
  currentGistId,
  gists,
  subjects = [],
  currentSubjectId = null,
  bodyDraft,
  disabled = false,
  getGistLinkUrl,
  onBodyDraftChange,
}) => {
  const candidates = useMemo(
    () =>
      gists
        .filter((gist) => gist.id !== currentGistId)
        .slice()
        .sort((a, b) => a.title.localeCompare(b.title)),
    [currentGistId, gists],
  );
  const parentBySubjectId = useMemo(
    () => buildParentBySubjectId(subjects),
    [subjects],
  );
  const initialSubjectId = useMemo(
    () => getInitialSubjectId(currentSubjectId, candidates),
    [currentSubjectId, candidates],
  );
  const [isChooserOpen, setIsChooserOpen] = useState(false);
  const [selectedSubjectId, setSelectedSubjectId] = useState<number | null>(initialSubjectId);
  const [expandedSubjectIds, setExpandedSubjectIds] = useState<Set<number>>(
    () => new Set(getSubjectAncestorIds(initialSubjectId, parentBySubjectId)),
  );
  const [selectedGistId, setSelectedGistId] = useState<number | "">("");
  const [customLabel, setCustomLabel] = useState("");
  const [gistSearchQuery, setGistSearchQuery] = useState("");
  const [insertStatus, setInsertStatus] = useState<string | null>(null);
  const [chooserMode, setChooserMode] = useState<"existing" | "create">("existing");
  const [newDraftTitle, setNewDraftTitle] = useState("");
  const [newDraftSummary, setNewDraftSummary] = useState("");
  const [createAsChildDraft, setCreateAsChildDraft] = useState(true);
  const [createDraftError, setCreateDraftError] = useState<string | null>(null);

  const [createGistWithContent, { loading: isCreatingDraft }] = useMutation<
    CreateDraftResult,
    CreateDraftVariables
  >(CREATE_GIST_WITH_CONTENT);

  useEffect(() => {
    const nextSubjectId = getInitialSubjectId(currentSubjectId, candidates);
    setSelectedSubjectId((current) => current ?? nextSubjectId);
  }, [currentSubjectId, candidates]);

  useEffect(() => {
    if (typeof selectedSubjectId !== "number") return;
    const ancestorIds = getSubjectAncestorIds(selectedSubjectId, parentBySubjectId);
    setExpandedSubjectIds((current) => {
      const next = new Set(current);
      ancestorIds.forEach((id) => next.add(id));
      return next;
    });
  }, [selectedSubjectId, parentBySubjectId]);

  const normalizedSearch = gistSearchQuery.trim().toLowerCase();
  const subjectGists = useMemo(
    () =>
      typeof selectedSubjectId === "number"
        ? candidates.filter((gist) => gist.subjectId === selectedSubjectId)
        : candidates,
    [candidates, selectedSubjectId],
  );
  const filteredGists = useMemo(() => {
    const source = normalizedSearch ? candidates : subjectGists;
    if (!normalizedSearch) return source;
    return source.filter((gist) => gist.title.toLowerCase().includes(normalizedSearch));
  }, [candidates, normalizedSearch, subjectGists]);

  useEffect(() => {
    if (!filteredGists.length) {
      if (selectedGistId !== "") setSelectedGistId("");
      return;
    }

    const hasCurrentSelection = filteredGists.some((gist) => gist.id === selectedGistId);
    if (!hasCurrentSelection) {
      setSelectedGistId(filteredGists[0].id);
    }
  }, [filteredGists, selectedGistId]);

  useEffect(() => {
    if (!insertStatus) return undefined;
    const timeoutId = window.setTimeout(() => setInsertStatus(null), 2200);
    return () => window.clearTimeout(timeoutId);
  }, [insertStatus]);

  const selectedGist =
    filteredGists.find((gist) => gist.id === selectedGistId) || filteredGists[0] || null;
  const selectedSubject =
    typeof selectedSubjectId === "number"
      ? subjects.find((subject) => subject.id === selectedSubjectId) || null
      : null;
  const canInsert = Boolean(selectedGist && !disabled);
  const canCreateDraft = Boolean(
    selectedSubject && newDraftTitle.trim() && !disabled && !isCreatingDraft,
  );
  const visibleSubjectRows = useMemo(
    () => buildVisibleSubjectRows(subjects, expandedSubjectIds),
    [subjects, expandedSubjectIds],
  );
  const subjectBuckets = useMemo(() => buildSubjectBuckets(subjects), [subjects]);

  const chooseSubject = (subjectId: number) => {
    setSelectedSubjectId(subjectId);
    setGistSearchQuery("");
    setSelectedGistId("");
    setCreateDraftError(null);
  };

  const toggleSubjectExpanded = (subjectId: number) => {
    setExpandedSubjectIds((current) => {
      const next = new Set(current);
      if (next.has(subjectId)) {
        next.delete(subjectId);
      } else {
        next.add(subjectId);
      }
      return next;
    });
  };

  const insertLinkForGist = (targetGist: GistItem, labelOverride?: string) => {
    const label = (labelOverride?.trim() || customLabel.trim() || targetGist.title).slice(
      0,
      MAX_LINK_LABEL_LENGTH,
    );
    const href = getGistLinkUrl(targetGist);
    const linkHtml = buildLinkParagraph(label, href);

    onBodyDraftChange((currentBody) => appendLinkToBody(currentBody, linkHtml));
    setCustomLabel("");
    setInsertStatus("Gist link inserted at the end of the body.");
    setIsChooserOpen(false);
  };

  const insertLink = () => {
    if (!selectedGist) return;
    insertLinkForGist(selectedGist);
  };

  const createDraftAndInsertLink = async () => {
    const title = truncateClean(newDraftTitle, MAX_DRAFT_TITLE_LENGTH);
    if (!selectedSubject || !title) return;

    setCreateDraftError(null);

    try {
      const result = await createGistWithContent({
        variables: {
          input: {
            title,
            subjectId: selectedSubject.id,
            ...(createAsChildDraft ? { parentId: currentGistId } : {}),
            point: buildDraftSummary(title, newDraftSummary),
            body: buildDraftBody(title),
            status: "DRAFT",
          },
        },
        refetchQueries: [
          { query: GET_ALL_SUBJECTS },
          { query: GET_ALL_GISTS },
          { query: GET_MY_DRAFT_GISTS },
          { query: GET_GISTS_BY_SUBJECT, variables: { subjectId: selectedSubject.id } },
        ],
        awaitRefetchQueries: true,
      });

      const created = result.data?.createGistWithContent;
      if (!created) {
        throw new Error("The draft was not returned by the server.");
      }

      insertLinkForGist(
        {
          id: created.id,
          publicId: created.publicId,
          title: created.title,
          slug: created.slug ?? null,
          subjectId: created.subjectId,
          parentId: created.parentId ?? null,
          status: created.status,
        },
        customLabel.trim() || created.title,
      );
      setNewDraftTitle("");
      setNewDraftSummary("");
      setCreateAsChildDraft(true);
      setInsertStatus("Draft created and link inserted at the end of the body.");
    } catch (error: any) {
      setCreateDraftError(error?.message || "Failed to create draft gist.");
    }
  };

  const openChooser = () => {
    setIsChooserOpen(true);
    setCreateDraftError(null);
  };

  if (!candidates.length && !subjects.length) {
    return (
      <div className={styles.internalGistLinkCompact}>
        <button type="button" className={styles.articleInsertLinkButton} disabled>
          Insert link
        </button>
        <span>No gists or subjects are available yet.</span>
      </div>
    );
  }

  return (
    <div className={styles.internalGistLinkCompact} aria-label="Insert internal Gist link">
      <button
        type="button"
        className={styles.articleInsertLinkButton}
        disabled={disabled}
        onClick={openChooser}
      >
        Insert link
      </button>
      {insertStatus && <span className={styles.internalGistLinkStatus}>{insertStatus}</span>}

      {isChooserOpen && (
        <div className={styles.internalGistLinkModalBackdrop} role="presentation">
          <div
            className={styles.internalGistLinkModal}
            role="dialog"
            aria-modal="true"
            aria-label="Insert Gist link"
          >
            <div className={styles.internalGistLinkModalHeader}>
              <div>
                <strong>Insert Gist link</strong>
                <p>
                  Browse the subject tree, choose an existing target, or create
                  a private draft target and insert its workspace link.
                </p>
              </div>
              <button
                type="button"
                className={styles.internalGistLinkCloseButton}
                aria-label="Close Insert Gist link"
                onClick={() => setIsChooserOpen(false)}
              >
                ×
              </button>
            </div>

            <div className={styles.internalGistLinkModalGrid}>
              <aside className={styles.internalGistSubjectPane} aria-label="Subject tree">
                <div className={styles.internalGistPaneHeader}>Subject tree</div>
                <div className={styles.internalGistSubjectTree}>
                  {visibleSubjectRows.length ? (
                    visibleSubjectRows.map(({ subject, depth }) => {
                      const children = subjectBuckets[String(subject.id)] || [];
                      const hasChildren = children.length > 0;
                      const isExpanded = expandedSubjectIds.has(subject.id);
                      const isSelected = selectedSubjectId === subject.id;
                      const directCount = getSubjectDirectGistCount(candidates, subject.id);

                      return (
                        <div
                          key={`internal-link-subject-${subject.id}`}
                          className={`${styles.internalGistSubjectRow} ${isSelected ? styles.internalGistSubjectRowActive : ""}`}
                          style={{ "--depth": depth } as React.CSSProperties}
                        >
                          <button
                            type="button"
                            className={styles.internalGistSubjectToggle}
                            disabled={!hasChildren}
                            aria-label={
                              hasChildren
                                ? isExpanded
                                  ? `Collapse ${subject.title}`
                                  : `Expand ${subject.title}`
                                : `${subject.title} has no child subjects`
                            }
                            onClick={(event) => {
                              event.stopPropagation();
                              if (hasChildren) toggleSubjectExpanded(subject.id);
                            }}
                          >
                            {hasChildren ? (isExpanded ? "⌄" : "›") : ""}
                          </button>
                          <button
                            type="button"
                            className={styles.internalGistSubjectButton}
                            onClick={() => chooseSubject(subject.id)}
                          >
                            <span className={styles.folderIcon} aria-hidden="true" />
                            <span>{subject.title}</span>
                            <small>{directCount} gists</small>
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <div className={styles.internalGistEmptyState}>No subjects available.</div>
                  )}
                </div>
              </aside>

              <section className={styles.internalGistTargetPane} aria-label="Target gist">
                <div className={styles.internalGistPaneHeader}>Target gist</div>
                <div className={styles.internalGistModeTabs} role="tablist" aria-label="Insert link mode">
                  <button
                    type="button"
                    className={chooserMode === "existing" ? styles.internalGistModeTabActive : undefined}
                    onClick={() => setChooserMode("existing")}
                  >
                    Existing gist
                  </button>
                  <button
                    type="button"
                    className={chooserMode === "create" ? styles.internalGistModeTabActive : undefined}
                    onClick={() => setChooserMode("create")}
                  >
                    Create draft target
                  </button>
                </div>

                {chooserMode === "existing" ? (
                  <>
                    <label className={styles.internalGistSearchLabel}>
                      <span>Search target gists</span>
                      <input
                        type="search"
                        value={gistSearchQuery}
                        placeholder="Search across all gists…"
                        onChange={(event) => setGistSearchQuery(event.target.value)}
                      />
                    </label>

                    <div className={styles.internalGistTargetList}>
                      {filteredGists.length ? (
                        filteredGists.map((gist) => {
                          const isSelected = selectedGist?.id === gist.id;
                          return (
                            <button
                              key={`internal-link-gist-${gist.id}`}
                              type="button"
                              className={`${styles.internalGistTargetRow} ${isSelected ? styles.internalGistTargetRowActive : ""}`}
                              onClick={() => setSelectedGistId(gist.id)}
                            >
                              <span className={styles.fileIcon} aria-hidden="true" />
                              <span>{gist.title}</span>
                              {isDraftGist(gist) && <b>Draft</b>}
                            </button>
                          );
                        })
                      ) : (
                        <div className={styles.internalGistEmptyState}>
                          No matching gists in this subject. Choose another subject,
                          search by title, or create a draft target.
                        </div>
                      )}
                    </div>

                    <div className={styles.internalGistLinkFooter}>
                      <label>
                        <span>Optional link label</span>
                        <input
                          type="text"
                          value={customLabel}
                          maxLength={MAX_LINK_LABEL_LENGTH}
                          disabled={disabled}
                          placeholder={selectedGist?.title || "Use gist title"}
                          onChange={(event) => setCustomLabel(event.target.value)}
                        />
                      </label>
                      <div className={styles.internalGistLinkFooterActions}>
                        <button type="button" onClick={() => setIsChooserOpen(false)}>
                          Cancel
                        </button>
                        <button type="button" disabled={!canInsert} onClick={insertLink}>
                          Insert link
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className={styles.internalGistCreateDraftPanel}>
                    <div className={styles.internalGistCreateDraftIntro}>
                      <strong>Create a private draft target</strong>
                      <p>
                        The new draft will be created in the selected subject
                        {selectedSubject ? `: ${selectedSubject.title}` : ""}. Its link will be inserted into this body draft immediately.
                      </p>
                    </div>

                    <label>
                      <span>Draft title</span>
                      <input
                        type="text"
                        value={newDraftTitle}
                        maxLength={MAX_DRAFT_TITLE_LENGTH}
                        disabled={disabled || isCreatingDraft}
                        placeholder="Title of the missing Gist"
                        onChange={(event) => setNewDraftTitle(event.target.value)}
                      />
                    </label>

                    <label>
                      <span>Draft summary</span>
                      <textarea
                        value={newDraftSummary}
                        maxLength={MAX_DRAFT_SUMMARY_LENGTH}
                        disabled={disabled || isCreatingDraft}
                        placeholder="Optional one-sentence summary. If empty, a placeholder summary is created."
                        onChange={(event) => setNewDraftSummary(event.target.value)}
                      />
                    </label>

                    <label>
                      <span>Optional link label</span>
                      <input
                        type="text"
                        value={customLabel}
                        maxLength={MAX_LINK_LABEL_LENGTH}
                        disabled={disabled || isCreatingDraft}
                        placeholder={newDraftTitle.trim() || "Use draft title"}
                        onChange={(event) => setCustomLabel(event.target.value)}
                      />
                    </label>

                    <label className={styles.internalGistCheckboxRow}>
                      <input
                        type="checkbox"
                        checked={createAsChildDraft}
                        disabled={disabled || isCreatingDraft}
                        onChange={(event) => setCreateAsChildDraft(event.target.checked)}
                      />
                      <span>Make this draft a direct child of the current gist.</span>
                    </label>

                    {selectedSubject && selectedSubject.id !== currentSubjectId && createAsChildDraft && (
                      <div className={styles.internalGistSoftWarning}>
                        This creates a child draft of the current gist, but places it in the selected subject. Uncheck the child option if this should only be a cross-subject reference.
                      </div>
                    )}

                    {createDraftError && (
                      <div className={styles.internalGistCreateError}>{createDraftError}</div>
                    )}

                    <div className={styles.internalGistLinkFooterActions}>
                      <button type="button" onClick={() => setIsChooserOpen(false)}>
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={!canCreateDraft}
                        onClick={createDraftAndInsertLink}
                      >
                        {isCreatingDraft ? "Creating…" : "Create draft + insert link"}
                      </button>
                    </div>
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
